import { boxesApi, imagesApi } from '../services/annotatorApi';
import type { Class as ClassModel } from '../types/api';
import { colorForClassIndex } from './classColors';

/**
 * renderAnnotatedImageToBlob — same drawing logic as renderAnnotatedImage,
 * but returns a Blob instead of triggering a browser download. Used by
 * the bulk-zip path so each rendered PNG can be packed into JSZip.
 */
export async function renderAnnotatedImageToBlob(args: {
    imageId: string;
    orgId: string | undefined;
    classes: ReadonlyArray<ClassModel>;
    signal?: AbortSignal;
}): Promise<Blob> {
    const { imageId, orgId, classes, signal } = args;
    if (signal?.aborted) throw new Error('aborted');

    const boxes = await boxesApi.list(imageId, orgId);
    if (signal?.aborted) throw new Error('aborted');
    const bytesUrl = imagesApi.bytesUrl(imageId, orgId);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = bytesUrl;
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('image_load_failed'));
    });
    if (signal?.aborted) throw new Error('aborted');

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas_2d_unavailable');
    ctx.drawImage(img, 0, 0);

    const baseDim = Math.min(canvas.width, canvas.height);
    const strokeWidth = Math.max(2, Math.round(baseDim / 400));
    const fontSize = Math.max(14, Math.round(baseDim / 50));
    ctx.lineWidth = strokeWidth;
    ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
    ctx.textBaseline = 'top';

    for (const b of boxes) {
        const cls = classes.find((c) => c.class_index === b.class_index);
        const color = cls?.color || colorForClassIndex(b.class_index);
        const name = cls?.name ?? `class ${b.class_index}`;
        const label = b.label ? `${b.label} · ${name}` : name;
        const w = b.width * canvas.width;
        const h = b.height * canvas.height;
        const x = b.x_center * canvas.width - w / 2;
        const y = b.y_center * canvas.height - h / 2;
        ctx.strokeStyle = color;
        ctx.strokeRect(x, y, w, h);
        const padX = Math.max(4, Math.round(fontSize / 4));
        const padY = Math.max(2, Math.round(fontSize / 6));
        const tw = ctx.measureText(label).width + padX * 2;
        const th = fontSize + padY * 2;
        const chipX = Math.max(0, Math.min(x, canvas.width - tw));
        const chipY = y - th >= 0 ? y - th : y;
        ctx.fillStyle = color;
        ctx.fillRect(chipX, chipY, tw, th);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(label, chipX + padX, chipY + padY);
    }

    const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
    );
    if (!blob) throw new Error('canvas_export_failed');
    return blob;
}

/**
 * renderAnnotatedImage — fetches an image's bytes + its boxes, draws them
 * onto an HTMLCanvasElement at the image's natural resolution, and triggers
 * a browser download of the resulting PNG.
 *
 * This is QA / review-only — it is NOT the format the trainer consumes.
 * The trainer takes raw images + the YOLO `.txt` labels in the snapshot
 * ZIP (see `/api/v1/snapshots/:id/download`). Burning boxes into pixels
 * would destroy the training signal.
 *
 * Browser CORS note: backend serves `image/bytes` with `*` ACAO, so
 * `crossOrigin = 'anonymous'` lets us read the pixels back out of the
 * canvas without tainting it. If that ever fails, the user will see a
 * generic error toast and we'd need to add a server-side render endpoint.
 */
export async function renderAnnotatedImage(args: {
    imageId: string;
    orgId: string | undefined;
    filename: string;
    classes: ReadonlyArray<ClassModel>;
}): Promise<void> {
    const { imageId, orgId, filename, classes } = args;

    const boxes = await boxesApi.list(imageId, orgId);
    const bytesUrl = imagesApi.bytesUrl(imageId, orgId);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = bytesUrl;
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('image_load_failed'));
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas_2d_unavailable');
    ctx.drawImage(img, 0, 0);

    // Scale stroke width + font with image size so review burns are readable
    // on both phone photos (3000×4000) and webcam grabs (640×480).
    const baseDim = Math.min(canvas.width, canvas.height);
    const strokeWidth = Math.max(2, Math.round(baseDim / 400));
    const fontSize = Math.max(14, Math.round(baseDim / 50));
    ctx.lineWidth = strokeWidth;
    ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
    ctx.textBaseline = 'top';

    for (const b of boxes) {
        const cls = classes.find((c) => c.class_index === b.class_index);
        const color = cls?.color || colorForClassIndex(b.class_index);
        const name = cls?.name ?? `class ${b.class_index}`;
        const label = b.label ? `${b.label} · ${name}` : name;

        const w = b.width * canvas.width;
        const h = b.height * canvas.height;
        const x = b.x_center * canvas.width - w / 2;
        const y = b.y_center * canvas.height - h / 2;

        // Box outline.
        ctx.strokeStyle = color;
        ctx.strokeRect(x, y, w, h);

        // Label chip — colored band above the box (or below if it'd clip
        // off the top of the canvas).
        const padX = Math.max(4, Math.round(fontSize / 4));
        const padY = Math.max(2, Math.round(fontSize / 6));
        const tw = ctx.measureText(label).width + padX * 2;
        const th = fontSize + padY * 2;
        const chipX = Math.max(0, Math.min(x, canvas.width - tw));
        const chipY = y - th >= 0 ? y - th : y;
        ctx.fillStyle = color;
        ctx.fillRect(chipX, chipY, tw, th);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(label, chipX + padX, chipY + padY);
    }

    const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
    );
    if (!blob) throw new Error('canvas_export_failed');

    const safeName = filename.replace(/\.[^.]+$/, '') + '_annotated.png';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
